import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  asNonNullArray,
  asObjectArray,
  asObjectOrNull,
  asStringOrNull,
  findSelectedById,
  findSelectedByObject,
  findSelectedManyByIds,
  findSelectedManyByObjects,
} from '@/components/select/selection';
import { arrayEqual } from '@/utils/array';

interface Item {
  id: string;
}

interface SingleSelectState<T extends Item> {
  selected: T | null;
  isInitialized: boolean;
}

interface MultiSelectState<T extends Item> {
  selected: T[];
  isInitialized: boolean;
}

export type SelectBoxState<T extends Item> =
  | (SingleSelectState<T> & { multiple?: false })
  | (MultiSelectState<T> & { multiple: true });

export interface SingleSelectProps<T extends Item> {
  value: T | string | null;
  onSelect: (selected: T | null, items?: T[]) => void;
}

export interface MultiSelectProps<T extends Item> {
  value: T[] | string[];
  onSelect: (selected: T[], items?: T[]) => void;
}

export type SelectBoxProps<T extends Item> =
  | (SingleSelectProps<T> & { multiple?: false })
  | (MultiSelectProps<T> & { multiple: true });

interface SelectBox<T extends Item> {
  handleChange: (value: T | (T | null)[] | null) => void;
  handleDeselect: () => void;
  state: SelectBoxState<T>;
}

const createInitialState = <T extends Item>(props: SelectBoxProps<T>): SelectBoxState<T> => {
  if (props.multiple) {
    return {
      selected: asObjectArray<T>(props.value),
      isInitialized: false,
      multiple: true,
    };
  }

  return {
    selected: asObjectOrNull<T>(props.value),
    isInitialized: false,
  };
};

const markInitialized = <T extends Item>(setState: Dispatch<SetStateAction<SelectBoxState<T>>>) => {
  setState((previousState) => ({
    ...previousState,
    isInitialized: true,
  }));
};

const applyMultiSelection = <T extends Item>(
  setState: Dispatch<SetStateAction<SelectBoxState<T>>>,
  onSelect: MultiSelectProps<T>['onSelect'],
  selected: T[],
  items?: T[],
) => {
  setState({
    selected,
    isInitialized: true,
    multiple: true,
  });
  onSelect(selected, items);
};

const applySingleSelection = <T extends Item>(
  setState: Dispatch<SetStateAction<SelectBoxState<T>>>,
  onSelect: SingleSelectProps<T>['onSelect'],
  selected: T | null,
  items?: T[],
) => {
  setState({
    selected,
    isInitialized: true,
  });
  onSelect(selected, items);
};

const useSyncMultiSelection = <T extends Item>(
  data: T[] | undefined,
  props: SelectBoxProps<T>,
  defaultValue: 'first' | 'all' | 'none' | undefined,
  stateRef: MutableRefObject<SelectBoxState<T>>,
  previousValueRef: MutableRefObject<SelectBoxProps<T>['value']>,
  setState: Dispatch<SetStateAction<SelectBoxState<T>>>,
) => {
  useEffect(() => {
    const currentState = stateRef.current;
    const hasExternalValueChanged = previousValueRef.current !== props.value;
    previousValueRef.current = props.value;

    if (!data || data.length === 0 || !props.multiple || !currentState.multiple) {
      return;
    }

    if (hasExternalValueChanged || !currentState.isInitialized) {
      const selectedFromIds = findSelectedManyByIds(data, props.value);
      if (selectedFromIds.length > 0 && !arrayEqual<T>(selectedFromIds, currentState.selected)) {
        applyMultiSelection(setState, props.onSelect, selectedFromIds, data);
        return;
      }

      const selectedFromObjects = findSelectedManyByObjects<T>(props.value);
      if (
        selectedFromObjects.length > 0 &&
        !arrayEqual<T>(selectedFromObjects, currentState.selected)
      ) {
        applyMultiSelection(setState, props.onSelect, selectedFromObjects, data);
        return;
      }
    }

    if (currentState.isInitialized) {
      return;
    }

    if (defaultValue === 'all') {
      applyMultiSelection(setState, props.onSelect, data, data);
      return;
    }

    markInitialized(setState);
  }, [data, props, defaultValue, setState, stateRef, previousValueRef]);
};

const useSyncSingleSelection = <T extends Item>(
  data: T[] | undefined,
  props: SelectBoxProps<T>,
  defaultValue: 'first' | 'all' | 'none' | undefined,
  stateRef: MutableRefObject<SelectBoxState<T>>,
  previousValueRef: MutableRefObject<SelectBoxProps<T>['value']>,
  setState: Dispatch<SetStateAction<SelectBoxState<T>>>,
) => {
  useEffect(() => {
    const currentState = stateRef.current;
    const hasExternalValueChanged = previousValueRef.current !== props.value;
    previousValueRef.current = props.value;

    if (!data || props.multiple || currentState.multiple) {
      return;
    }

    if (hasExternalValueChanged || !currentState.isInitialized) {
      if (props.value === null && currentState.selected) {
        applySingleSelection(setState, props.onSelect, null);
        return;
      }

      const selectedFromId = findSelectedById(data, props.value);
      if (selectedFromId !== null) {
        if (selectedFromId !== currentState.selected) {
          applySingleSelection(setState, props.onSelect, selectedFromId);
        }
        return;
      }

      const selectedFromObject = findSelectedByObject(data, props.value);
      if (selectedFromObject !== null) {
        if (selectedFromObject !== currentState.selected) {
          applySingleSelection(setState, props.onSelect, selectedFromObject);
        }
        return;
      }

      const isResolvableValue =
        asStringOrNull<T>(props.value) !== null || asObjectOrNull(props.value) !== null;
      if (isResolvableValue && currentState.selected) {
        applySingleSelection(setState, props.onSelect, null);
        return;
      }
    }

    if (currentState.isInitialized) {
      return;
    }

    if (defaultValue === 'first') {
      applySingleSelection(setState, props.onSelect, data[0]);
      return;
    }

    markInitialized(setState);
  }, [data, props, defaultValue, setState, stateRef, previousValueRef]);
};

const useSelectBox = <T extends Item>(
  data: T[] | undefined,
  props: SelectBoxProps<T>,
  defaultValue?: 'first' | 'all' | 'none',
): SelectBox<T> => {
  const [state, setState] = useState<SelectBoxState<T>>(createInitialState(props));
  const stateRef = useRef(state);
  const previousValueRef = useRef(props.value);
  stateRef.current = state;

  const handleChange = (nextValue: T | (T | null)[] | null) => {
    if (props.multiple) {
      applyMultiSelection(setState, props.onSelect, asNonNullArray<T>(nextValue), data);
      return;
    }

    applySingleSelection(
      setState,
      props.onSelect,
      Array.isArray(nextValue) ? null : nextValue,
      data,
    );
  };

  const handleDeselect = () => {
    if (props.multiple) {
      applyMultiSelection(setState, props.onSelect, []);
      return;
    }

    applySingleSelection(setState, props.onSelect, null);
  };

  useSyncMultiSelection(data, props, defaultValue, stateRef, previousValueRef, setState);
  useSyncSingleSelection(data, props, defaultValue, stateRef, previousValueRef, setState);

  return {
    handleChange,
    handleDeselect,
    state,
  };
};

export default useSelectBox;
